/**
 * Web-Mercator zoom derivation for the MapLibre vector OVERLAY.
 *
 * The overlay paints PMTiles / GeoJSON vectors on a transparent canvas above
 * the primary 3D engine (Giro3D). Giro3D's `cameraChange` carries a geographic
 * `lonLat` and a camera `height` (metres above ground) but **no** Web-Mercator
 * `zoom`. Without a zoom the overlay scale stays frozen at its mount-time
 * default, so vectors render at the wrong scale and effectively disappear once
 * the user orbits / zooms in 3D — the classic "vector toggles on but nothing
 * shows in the main viewer" bug (data is fine, the Preview map proves it; the
 * overlay is just scaled wrong).
 *
 * This maps a perspective-camera height to the MapLibre zoom whose ground
 * resolution matches what the 3D camera sees, so the overlay tracks scale and
 * the vectors land in view. It is deliberately an approximation: the overlay
 * stays top-down while the 3D view may be tilted, and the camera FOV is assumed
 * (three.js default). The goal is "vectors visible at roughly the right scale",
 * not pixel-perfect registration.
 */

/** Web-Mercator ground resolution at zoom 0, equator, 256px tiles (m/px). */
export const WEB_MERCATOR_MPP_Z0 = 156543.03392804097;

/** three.js PerspectiveCamera default vertical FOV — what Giro3D uses. */
export const DEFAULT_FOV_Y_RAD = (50 * Math.PI) / 180;

export interface MercatorZoomInput {
  /** Camera height above ground, metres. */
  heightMeters: number;
  /** Latitude of the view centre, degrees. */
  latDeg: number;
  /** Overlay canvas height, CSS pixels. */
  viewportHeightPx: number;
  /** Vertical field of view, radians. Defaults to the three.js 50° default. */
  fovYRad?: number;
}

/**
 * Map a 3D perspective-camera height to the equivalent Web-Mercator zoom.
 *
 * @returns a zoom clamped to `[0, 22]`, or `null` when inputs are unusable
 *   (non-positive height / viewport, NaN result). Callers should preserve the
 *   overlay's current zoom when this returns `null`.
 */
export function mercatorZoomForCameraHeight(input: MercatorZoomInput): number | null {
  const { heightMeters, latDeg, viewportHeightPx } = input;
  const fovY = typeof input.fovYRad === 'number' && input.fovYRad > 0
    ? input.fovYRad
    : DEFAULT_FOV_Y_RAD;
  if (!(heightMeters > 0) || !(viewportHeightPx > 0)) return null;
  const lat = Math.max(-85, Math.min(85, latDeg));
  // Vertical ground span (m) the perspective camera covers at this height.
  const groundSpanM = 2 * heightMeters * Math.tan(fovY / 2);
  if (!(groundSpanM > 0)) return null;
  const metersPerPixel = groundSpanM / viewportHeightPx;
  if (!(metersPerPixel > 0)) return null;
  // metersPerPixel = WEB_MERCATOR_MPP_Z0 * cos(lat) / 2^zoom  →  solve for zoom.
  const zoom = Math.log2(
    (WEB_MERCATOR_MPP_Z0 * Math.cos((lat * Math.PI) / 180)) / metersPerPixel,
  );
  if (!Number.isFinite(zoom)) return null;
  return Math.max(0, Math.min(22, zoom));
}

/**
 * Inverse of {@link mercatorZoomForCameraHeight}: camera height (metres above
 * ground) that produces the requested Web-Mercator zoom for a given latitude
 * and viewport. Used by "Zoom to layer" so every engine lands at the same
 * operator-facing zoom (e.g. 14).
 */
export function cameraHeightForMercatorZoom(input: {
  zoom: number;
  latDeg: number;
  viewportHeightPx: number;
  fovYRad?: number;
}): number | null {
  const { zoom, latDeg, viewportHeightPx } = input;
  const fovY = typeof input.fovYRad === 'number' && input.fovYRad > 0
    ? input.fovYRad
    : DEFAULT_FOV_Y_RAD;
  if (!(viewportHeightPx > 0) || !Number.isFinite(zoom)) return null;
  const lat = Math.max(-85, Math.min(85, latDeg));
  const metersPerPixel =
    (WEB_MERCATOR_MPP_Z0 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
  if (!(metersPerPixel > 0)) return null;
  const groundSpanM = metersPerPixel * viewportHeightPx;
  const heightMeters = groundSpanM / (2 * Math.tan(fovY / 2));
  if (!Number.isFinite(heightMeters) || !(heightMeters > 0)) return null;
  return heightMeters;
}
